/**
 * @file PickingHistoryPanel.tsx
 * @description Режим «Журнал» экрана заданий на сборку (phase19.14, довесок;
 * phase19.23, A1; phase19.31 шаг О4 — те же общие части, что у очереди).
 *
 * Проведённые и отменённые задания страницами по `finishedAt desc`: очередь
 * их скрывает, а менеджеру нужно видеть, кто и когда собрал заказ. Состав
 * данных прежний; вид — `ResponsiveList` (таблица от 1024, компактный
 * список до 1024). «Показать ещё» вместо номеров страниц: на складе листают
 * с телефона.
 */

import { Warehouse } from "lucide-react";
import { useMemo, useState } from "react";

import { pickingHistoryColumns } from "./picking-columns";
import { historyEmptySpec, historyFilteredSpec } from "./picking-empty";
import type { PickingView } from "./picking-view";
import { PickingPageHeader } from "./PickingPageHeader";
import { PickingQueueFilters } from "./PickingQueueFilters";
import { usePickingHistory, type HistoryTab } from "./usePickingHistory";
import { Button } from "../../ui/button";
import type { FilterChip } from "../../ui/FilterChips";
import { OptionSheet } from "../../ui/OptionSheet";
import { ResponsiveList } from "../../ui/ResponsiveList";
import { BELOW_LG_QUERY, useMediaQuery } from "../../../hooks/useMediaQuery";
import { useWarehouseMode } from "../../../hooks/useWarehouseMode";

/** Пилюли журнала — данными, а не ветками по месту отрисовки. */
const HISTORY_CHIPS: FilterChip<HistoryTab>[] = [
  { value: "all", label: "Все" },
  { value: "done", label: "Проведённые" },
  { value: "cancelled", label: "Отменённые" },
];

interface Props {
  view: PickingView;
  onViewChange: (view: PickingView) => void;
  /** База маршрута WMS: `/shop/:slug/wms`. */
  basePath: string;
  /** Доска заказов — действие пустого состояния. */
  ordersBoardPath: string;
  currentUserId: string | null;
}

export function PickingHistoryPanel({ view, onViewChange, basePath, ordersBoardPath, currentUserId }: Props) {
  const history = usePickingHistory();
  const compact = useMediaQuery(BELOW_LG_QUERY);
  const warehouseMode = useWarehouseMode();
  const [warehouseSheet, setWarehouseSheet] = useState(false);
  const columns = useMemo(() => pickingHistoryColumns(currentUserId), [currentUserId]);
  const warehouseOptions = [{ value: "", label: "Все склады" }, ...history.warehouses.map((w) => ({ value: w.id, label: w.name }))];
  const warehouseName = warehouseOptions.find((o) => o.value === history.warehouseId)?.label ?? "Все склады";

  return (
    <>
      <PickingPageHeader
        view={view}
        onViewChange={onViewChange}
        onRefresh={history.refresh}
        loading={history.loading}
        warehouseAction={
          compact
            ? { key: "warehouse", label: `Склад: ${warehouseName}`, icon: Warehouse, run: () => setWarehouseSheet(true) }
            : undefined
        }
      />

      <PickingQueueFilters
        chips={HISTORY_CHIPS}
        tab={history.tab}
        onTabChange={history.setTab}
        search={history.search}
        onSearchChange={history.setSearch}
        warehouses={history.warehouses}
        warehouseId={history.warehouseId}
        onWarehouseChange={history.setWarehouseId}
        loading={history.loading}
        showWarehouse={!compact}
        autoFocus={warehouseMode}
        searchPlaceholder="Номер заказа в журнале"
      />

      <ResponsiveList
        aria-label="Журнал заданий на сборку"
        rows={history.visible}
        columns={columns}
        rowKey={(task) => task.id}
        // `from=history`: «Назад» из задания вернёт в журнал, а не в очередь.
        rowHref={(task) => `${basePath}/picking/${task.id}?from=history`}
        rowLabel={(task) => `Задание ${task.number} по заказу ${task.orderNumber}`}
        rowSize="double"
        compactRowSize="tall"
        tableMinWidth="min-w-[900px]"
        loading={history.loading}
        error={history.error}
        onRetry={history.refresh}
        filtered={!!history.search}
        empty={historyEmptySpec(ordersBoardPath)}
        filteredEmpty={historyFilteredSpec(() => history.setSearch(""))}
      />

      {history.visible.length > 0 ? (
        history.hasMore ? (
          <Button variant="outline" className="self-center" onClick={history.loadMore} loading={history.loadingMore}>
            Показать ещё
          </Button>
        ) : (
          <p className="type-caption text-center text-on-surface">Показаны все задания: {history.total}</p>
        )
      ) : null}

      <OptionSheet
        open={warehouseSheet}
        onOpenChange={setWarehouseSheet}
        title="Склад"
        value={history.warehouseId}
        options={warehouseOptions}
        onChange={history.setWarehouseId}
      />
    </>
  );
}
